import getAuthSession from "../auth/browser/getAuthSession";
import refreshTokenFetchMiddleware from "./middleware/refreshTokenFetchMiddleware";
import { FetchMethod } from "./Types/FetchMethod";

export interface FetchPlusError {
    message: string;
    response: {
        errors?: {
            [key: string]: string[];
        };
    };
}

/**
 * This fetch plus attaches our access token to the request headers, and also
 * handles refreshing the access token if it is expired.
 * on refresh token failure, it will clear the auth session.
 * on refresh token success it will attempt to retry the original request
 * @param url
 * @param method
 * @param body
 * @param includeAccessToken
 * @param returnJson in some cases we want to skip returning json and just return the response
 * @returns
 */
const fetchPlus = async <BodyData>(
    url: string,
    method: FetchMethod,
    body?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    includeAccessToken = true,
    returnJson = true
): Promise<BodyData> => {
    const session = getAuthSession();
    const accessToken = session?.accessToken;

    return fetch(url, {
        method, // *GET, POST, PUT, DELETE, etc.
        // assign the token as bearer token on your request headers
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: includeAccessToken && accessToken ? `Bearer ${accessToken}` : "",
        },
        body: JSON.stringify(body),
    })
        .then((res) => refreshTokenFetchMiddleware(res, async () => fetchPlus(url, method, body, includeAccessToken, false)))
        .then(async (res) => {
            // in SWR we need an error thrown if the status code is not in the range 200-299.
            // We still try to parse and throw it.
            if (!res.ok) {
                // eslint-disable-next-line no-throw-literal
                throw {
                    message: "An error occurred while fetching the data.",
                    response: returnJson ? await res.json() : res,
                };
            }

            return returnJson ? res.json() : res;
        });
};

export default fetchPlus;
